import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// iter122 — Session Keep-Alive. Refresh the JWT this many ms before its exp.
// 5 minutes gives us plenty of slack even on slow networks while still
// cutting the "silent 24-hour logout" problem down to zero for active users.
const REFRESH_LEEWAY_MS = 5 * 60 * 1000;
// Safety floor — if the leeway math produces something <= 0 (e.g. a token
// that already expired or has <5 min left) schedule the next attempt in 10s.
const MIN_REFRESH_DELAY_MS = 10 * 1000;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// iter122 — JWT exp decoder. Returns epoch-ms or null if the token looks
// malformed. Pure base64url parse, no third-party dep so bundle size stays
// flat.
const _decodeTokenExpMs = (jwt) => {
  if (!jwt || typeof jwt !== 'string') return null;
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    const json = JSON.parse(atob(padded));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch (_) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // iter120 — one-shot guard so repeated 401s from a page's parallel fetches
  // produce exactly one "Session expired" toast + one redirect.
  const sessionExpiredRef = useRef(false);
  // iter122 — holds the refresh setTimeout handle so we can cancel on logout
  // / token-change and reschedule cleanly.
  const refreshTimerRef = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setToken(null);
    setUser(null);
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    logout();
    try { toast.error('Session expired. Please sign in again.'); } catch (_) {}
    // Preserve current path so we can bounce back after re-login.
    try {
      const here = window.location.pathname + window.location.search;
      if (here && here !== '/login') {
        window.sessionStorage.setItem('post_login_redirect', here);
      }
    } catch (_) {}
    // Hard navigation forces a clean React tree & drops any in-flight requests.
    window.location.assign('/login');
  }, [logout]);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    // iter120 — Global 401 interceptor. Previously each page caught its own
    // 401 and showed a "Failed to load X" toast while the user remained
    // "logged in" with a dead token — triggering a cascade of failing pages.
    // Now any 401 from an authenticated request forces a single logout +
    // bounce back to /login (public /auth/* calls are exempt so bad-login
    // 401s still surface their own toast). iter122 — also skip /auth/refresh
    // so a refresh race during logout doesn't double-fire the expired flow.
    instance.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/forgot-password') || url.includes('/auth/reset-password') || url.includes('/auth/refresh');
        if (status === 401 && token && !isAuthRoute) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [token, handleSessionExpired]);

  // iter122 — Silent token refresh scheduler. Runs ~5 min before the JWT
  // expires. On success we swap the token in state + localStorage, which
  // triggers this effect to schedule the next refresh. On failure we leave
  // the old token alone and let the normal 401 flow take over at expiry.
  const scheduleRefresh = useCallback((currentToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const expMs = _decodeTokenExpMs(currentToken);
    if (!expMs) return;
    const delay = Math.max(expMs - Date.now() - REFRESH_LEEWAY_MS, MIN_REFRESH_DELAY_MS);
    refreshTimerRef.current = setTimeout(async () => {
      // Guard: another logout / token change may have happened mid-timeout.
      if (tokenRef.current !== currentToken) return;
      try {
        const resp = await axios.post(`${API}/auth/refresh`, null, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        const newToken = resp.data?.access_token;
        if (newToken && typeof newToken === 'string') {
          localStorage.setItem('token', newToken);
          setToken(newToken);  // triggers validateToken + reschedules
          sessionExpiredRef.current = false;
        }
      } catch (err) {
        // 401 here means the backend already considers the token dead —
        // the user will land on /login on the next request anyway. For
        // anything else just retry close to the real expiry.
        const status = err?.response?.status;
        // eslint-disable-next-line no-console
        console.warn('Token refresh failed:', status || err?.message);
        if (status !== 401 && status !== 403) {
          // Retry sooner on transient errors so we still catch the window.
          scheduleRefresh(currentToken);
        }
      }
    }, delay);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const validateToken = async () => {
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const response = await api().get('/auth/me');
        if (!cancelled) {
          setUser(response.data);
          sessionExpiredRef.current = false;  // reset guard on a healthy session
          scheduleRefresh(token);  // iter122 — arm the keep-alive timer
        }
      } catch (error) {
        // iter120 — only treat clear auth failures (401/403) as logout.
        // Transient network errors / cancelled requests / server 5xx should
        // NOT wipe the session, otherwise a navigation that cancels an
        // in-flight /auth/me logs the user out spuriously.
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          if (!cancelled) logout();
        } else {
          // eslint-disable-next-line no-console
          console.warn('Token validation request did not complete:', status || error?.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    validateToken();
    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [token, api, logout, scheduleRefresh]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    sessionExpiredRef.current = false;
    return userData;
  };

  const register = async (email, password, fullName, role = 'Operator') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      role,
    });
    return response.data;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, register, logout, api, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};
